<template>
  <bm-page
    v-bind="$attrs"
    :subtitle="computedSubtitle"
    @content-height-resized="onAvailableContentHeightUpdated"
  >
    <template #grid-controls>
      <slot
        name="grid-controls"
        :grid-configuration="gridConfiguration"
        :grid-state="gridState"
        :show-search="showSearch"
      >
        <bm-grid-control-panel
          :grid-configuration="gridConfiguration"
          :grid-state="gridState"
          :show-search="showSearch"
          :show-expand-all="showExpandAll"
          :all-rows-expanded="allRowsExpanded"
          :custom-print-title="customPrintTitle"
          @toggle-expand-all="$emit('toggle-expand-all')"
        >
          <template #additional-controls>
            <bm-btn
              v-if="mapMarkers"
              button-type="secondary"
              @click="toggleMap()"
            >
              <v-icon :left="$vuetify.breakpoint.smAndUp">map</v-icon>
              {{ $vuetify.breakpoint.smAndUp ? $m.map_view : "" }}
            </bm-btn>
          </template>
        </bm-grid-control-panel>
      </slot>
    </template>

    <v-container
      v-if="hasDataFilter"
      ref="dataFiltersRef"
      fluid
      px-0
      class="bm-grid-data-filter"
    >
      <v-layout>
        <v-flex>
          <slot name="grid-data-filter" />
        </v-flex>
      </v-layout>
    </v-container>

    <v-expand-transition>
      <v-container
        v-if="shouldShowMap && !useSideBySideMap"
        v-show="mapIsVisible"
        fluid
        pa-0
      >
        <v-layout>
          <v-flex>
            <slot name="map">
              <bm-map-loader
                class="gMap pb-4"
                :markers="mapMarkers"
                :visble="mapIsVisible"
                :update-bounds-on-marker-update="updateBoundsOnMarkerUpdate"
                :use-marker-clusterer="useMarkerClusterer"
                :polylines="polylines"
                @input="$emit('marker-clicked', $event)"
              />
            </slot>
          </v-flex>
        </v-layout>
      </v-container>
    </v-expand-transition>

    <v-container fluid pa-0 class="d-flex flex">
      <v-layout>
        <v-flex
          cols="12"
          :md="useSideBySideMap && shouldShowMap && mapIsVisible ? 6 : 12"
        >
          <v-container fluid pa-0>
            <v-row>
              <v-col v-if="$slots['above-grid']" cols="12">
                <slot name="above-grid" />
              </v-col>
              <v-col cols="12">
                <slot
                  name="grid"
                  :grid-state="gridState"
                  :grid-configuration="gridConfiguration"
                  :set-filter-summary="setFilterSummary"
                  :height="gridHeight"
                  :override-height="gridHeightWithUnits"
                  :initial-visible-rows="200"
                  :allow-select="allowSelect"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-flex>
        <v-slide-x-transition leave-absolute>
          <v-flex
            v-if="shouldShowMap && useSideBySideMap"
            v-show="mapIsVisible"
            cols="12"
            :md="mapIsVisible ? 6 : 0"
            class="pl-4"
          >
            <slot name="map">
              <bm-map-loader
                class="pb-4 gMap"
                :markers="mapMarkers"
                :visible="mapIsVisible"
                :update-bounds-on-marker-update="updateBoundsOnMarkerUpdate"
                :use-marker-clusterer="useMarkerClusterer"
                :polylines="polylines"
                @input="$emit('marker-clicked', $event)"
              />
            </slot>
          </v-flex>
        </v-slide-x-transition>
      </v-layout>
    </v-container>

    <slot />
  </bm-page>
</template>

<script lang="ts">
import BmPage from "@/components/pages/BmPage.vue";
import BmGridControlPanel from "@/components/controls/grid/BmGridControlPanel.vue";
import type {
  BmGridConfiguration,
  BmGridState,
} from "@/components/controls/grid/BmGridData";
import type { PropType } from "vue";
import { computed, defineComponent, ref } from "vue";
import type { MapMarker, Polyline } from "@/components/controls/map/maps";
import BmMapLoader from "@/components/controls/map/BmMapLoader.vue";
import { useVuetify } from "@/plugins/vuetify";

export default defineComponent({
  name: "BmGridPage",
  components: { BmPage, BmGridControlPanel, BmMapLoader },
  props: {
    subtitle: {
      type: String,
      default: "",
    },
    gridState: {
      type: Object as PropType<BmGridState>,
      required: true,
    },
    gridConfiguration: {
      type: Object as PropType<BmGridConfiguration>,
      required: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    mapMarkers: {
      type: Array as PropType<MapMarker[]>,
      required: false,
    },
    sideBySideMap: {
      type: Boolean,
      required: false,
      default: false,
    },
    updateBoundsOnMarkerUpdate: {
      type: Boolean,
      required: false,
      default: true,
    },
    showExpandAll: {
      type: Boolean,
      required: false,
      default: false,
    },
    allRowsExpanded: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    useMarkerClusterer: {
      type: Boolean,
      required: false,
      default: false,
    },
    polylines: {
      type: Array as PropType<Polyline[]>,
      required: false,
    },
    customPrintTitle: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
  },
  setup(props, context) {
    const { $vuetify } = useVuetify();

    const filterSummary = ref("");
    const availableContentHeight = ref<number | null>(null);
    const mapIsVisible = ref(false);
    const mapInitialised = ref(false);
    const dataFiltersRef = ref<any>(null);

    const hasDataFilter = computed(() => !!context.slots["grid-data-filter"]);

    const computedSubtitle = computed(
      () => props.subtitle || filterSummary.value
    );

    /**
     * Returns the maximum available space for a grid to sit within
     */
    const gridHeight = computed(() => {
      if (!availableContentHeight.value) {
        return undefined;
      }

      const dataFiltersHeight =
        hasDataFilter.value && dataFiltersRef.value
          ? (dataFiltersRef.value as HTMLDivElement).clientHeight
          : 0;

      const gridPadding = 10;
      return availableContentHeight.value - dataFiltersHeight - gridPadding;
    });

    const gridHeightWithUnits = computed(() =>
      gridHeight.value ? gridHeight.value + "px" : undefined
    );

    const shouldShowMap = computed(
      () => !!props.mapMarkers && mapInitialised.value
    );

    const useSideBySideMap = computed(
      () => $vuetify.breakpoint.mdAndUp && props.sideBySideMap
    );

    const setFilterSummary = (newFilterSummary: string) => {
      filterSummary.value = newFilterSummary;
    };

    const toggleMap = () => {
      mapIsVisible.value = !mapIsVisible.value;
      mapInitialised.value = true;
    };

    const onAvailableContentHeightUpdated = (
      newAvailableContentHeight: number
    ) => {
      availableContentHeight.value = newAvailableContentHeight;
    };

    return {
      filterSummary,
      availableContentHeight,
      mapIsVisible,
      mapInitialised,
      hasDataFilter,
      computedSubtitle,
      dataFiltersRef,
      gridHeight,
      gridHeightWithUnits,
      shouldShowMap,
      useSideBySideMap,
      setFilterSummary,
      toggleMap,
      onAvailableContentHeightUpdated,
    };
  },
});
</script>

<style lang="sass">

.gMap
  min-height: 40vh
</style>
